import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  AttachMoney as PriceIcon,
  Schedule as DurationIcon
} from '@mui/icons-material';
import FacilityAccessDisplay from './FacilityAccessDisplay';

const PlanDetailsModal = ({ plan, open, onClose, onSelect, currentUserPlan, isLoggedIn }) => {
  const { t } = useTranslation();

  if (!plan) return null;

  const formatPrice = (price) => {
    return `₺${price.toFixed(2)}`;
  };

  const formatDuration = (duration) => {
    if (duration === 1) return t('planDetails:duration.oneDay');
    if (duration < 30) return t('planDetails:duration.days', { count: duration });
    if (duration === 30) return t('planDetails:duration.oneMonth');
    if (duration < 365) return t('planDetails:duration.months', { count: Math.round(duration / 30) });
    return t('planDetails:duration.years', { count: Math.round(duration / 365) });
  };

  const isCurrentPlan = currentUserPlan === plan._id;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h2">
            {plan.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <PriceIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5" color="primary" sx={{ mr: 2 }}>
            {formatPrice(plan.price)}
          </Typography>
          <DurationIcon color="text.secondary" sx={{ mr: 1 }} />
          <Typography variant="body1" color="text.secondary">
            {t('planDetails:perDuration', { duration: formatDuration(plan.duration) })}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        <Typography variant="body1" paragraph>
          {plan.description}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          {t('planDetails:planFeatures')}
        </Typography>
        <List dense>
          {plan.features.map((feature, index) => (
            <ListItem key={index} sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={feature} />
            </ListItem>
          ))}
        </List>

        {plan.facilityAccess && plan.facilityAccess.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <FacilityAccessDisplay 
              facilityAccess={plan.facilityAccess}
              showDetailed={true}
            />
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Box sx={{ 
          p: 2, 
          bgcolor: 'primary.50', 
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'primary.200'
        }}>
          <Typography variant="h6" gutterBottom color="primary">
            {t('planDetails:planSummary')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('planDetails:summary.duration')}
              </Typography>
              <Typography variant="body1">
                {formatDuration(plan.duration)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('planDetails:summary.price')}
              </Typography>
              <Typography variant="body1" color="primary">
                {formatPrice(plan.price)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('planDetails:summary.features')}
              </Typography>
              <Typography variant="body1">
                {t('planDetails:summary.featuresCount', { count: plan.features.length })}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('planDetails:summary.facilityAccess')}
              </Typography>
              <Typography variant="body1">
                {t('planDetails:summary.facilityCount', { count: plan.facilityAccess?.length || 0 })}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit">
          {t('planDetails:buttons.close')}
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => onSelect(plan)}
          disabled={!isLoggedIn || isCurrentPlan}
          sx={{ ml: 2, minWidth: 140 }}
        >
          {!isLoggedIn ? t('planDetails:buttons.loginToSelect') :
           isCurrentPlan ? t('planDetails:buttons.currentPlan') :
           t('planDetails:buttons.selectThisPlan')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanDetailsModal;